/**
 * useRedirectIfCoreHandoff — bounce the marketing HomePage to the
 * golfer dashboard when the user is being handed off from Core.
 *
 * User report 2026-06-25: tapping "Fittings" in Core briefly renders
 * Fitting's marketing HomePage before the golfer dashboard mounts.
 * Core's goToFitting() sends them to `/golfer/dashboard?…` with auth
 * params, so React Router DOES match the dashboard route — but in a
 * handful of edge paths (cached cross-origin redirect, root-level
 * hand-off from a Core button that points at `/` rather than the
 * dashboard, etc.) the user lands on `/` first and sees the marketing
 * site before the auth handshake completes.
 *
 * This hook covers BOTH signals:
 *
 *   1. URL params from Core's handoff: `from=core`, `token=`, or
 *      `refresh_token=`. Present on cross-origin nav (Capacitor +
 *      future split-origin deploys).
 *   2. sessionStorage flag `golfauth_arrived_via=core` set by
 *      supabaseAuthReady when it sees `from=core`. Survives the URL
 *      strip that runs immediately after the handoff so the redirect
 *      still fires if HomePage mounts after supabaseAuthReady has
 *      already cleaned the URL.
 *
 * If either signal is present and the user is on `/`, bounce them to
 * the dashboard. The downstream GolferProtectedRoute renders
 * GolfAuthLoadingScreen until the auth store resolves, so the user
 * sees a spinner instead of marketing copy during the brief window.
 *
 * Returns true while the redirect decision is pending so callers can
 * render their own loading screen instead of the marketing UI in the
 * same paint.
 */

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Pure synchronous detection of a Core handoff via URL + sessionStorage.
 * Pulled out of the hook so it can run inside a useState initializer —
 * that's what gates the marketing UI on FIRST render. If this logic
 * lived inside useEffect (as in the original PR #84 shape), React would
 * commit one frame of marketing JSX before the effect ran, defeating
 * the flash suppression. Bugbot follow-up 2026-06-25.
 */
function detectCoreHandoff(targetPath: string): boolean {
  if (typeof window === 'undefined') return false;
  // Already at the target — no redirect needed, and we'd otherwise
  // bounce in place.
  if (window.location.pathname === targetPath) return false;

  const params = new URLSearchParams(window.location.search);
  const fromUrl =
    params.get('from') === 'core' ||
    params.has('token') ||
    params.has('refresh_token');
  if (fromUrl) return true;

  try {
    return sessionStorage.getItem('golfauth_arrived_via') === 'core';
  } catch {
    // sessionStorage can be unavailable in private-mode iOS Safari —
    // fall through and treat as "no handoff in flight."
    return false;
  }
}

export function useRedirectIfCoreHandoff(target = '/golfer/dashboard'): boolean {
  const navigate = useNavigate();
  const location = useLocation();
  // useState initializer runs synchronously on first render — pending
  // is true BEFORE React commits the first paint when the handoff
  // signal is present. The caller can return a loading screen instead
  // of the marketing JSX in the SAME paint, eliminating the one-frame
  // flash that the useEffect-only approach left behind.
  const [pending, setPending] = useState(() => detectCoreHandoff(target));

  // The actual navigate must happen in an effect (can't call during
  // render). When location catches up to the target between paints
  // we flip pending back to false so the caller renders normally.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (location.pathname === target) {
      if (pending) setPending(false);
      // Clear the handoff flag once we've landed — otherwise every
      // later visit to `/` in this tab shows a loading screen and
      // forces /golfer/dashboard (PWA reopen looks "stuck").
      try {
        sessionStorage.removeItem('golfauth_arrived_via');
      } catch {
        /* private mode */
      }
      return;
    }
    if (pending) {
      navigate(target, { replace: true });
    }
  }, [navigate, location.pathname, target, pending]);

  return pending;
}
